import { k, t } from '../../localization/t';
import { api } from '../serverClient';

// The Oracle at runtime, display side. Rules and every number live on
// the server in valkenhall-server/src/utils/oracle.js; this file names
// the omens, points at their tarot art and phrases the copy. Keep the
// ids, thresholds and Foresight arithmetic in lockstep.

export const BASE_MAX_FORESIGHT = 10;
export const OMEN_REROLL_COST = 1;
export const SIGHT_COST = { Ordinary: 1, Exceptional: 2, Elite: 3, Unique: 5 };
export const SIGHT_NODE = {
  Ordinary: 'oracle_look',
  Exceptional: 'oracle_exceptional',
  Elite: 'oracle_elite',
  Unique: 'oracle_unique',
};

// The plates live on the cards CDN (R2 bucket `valkenhall-cards`, $0 egress),
// like every card image; scripts/tarot-plates.cjs cuts them, wrangler puts
// them under mastery/tarot/. Nothing in the bundle.
const TAROT = 'https://cards.valkenhall.com/mastery/tarot';

// `text` takes {n}, the tier's threshold. `seat`: whose counter the omen
// reads; `both` sums the table.
export const OMENS = {
  fool: { title: k('The Fool'), art: `${TAROT}/fool.webp`, stat: 'damageTaken', seat: 'self', thresholds: [12, 18],
    text: k('You will take {n} or more damage this match.') },
  magician: { title: k('The Magician'), art: `${TAROT}/magician.webp`, stat: 'magics', seat: 'both', thresholds: [8, 10],
    text: k('{n} or more magics will be played this match.') },
  empress: { title: k('The Empress'), art: `${TAROT}/empress.webp`, stat: 'elites', seat: 'both', thresholds: [5, 8],
    text: k('{n} or more Elite cards will be played this match.') },
  priestess: { title: k('The High Priestess'), art: `${TAROT}/high-priestess.webp`, stat: 'healed', seat: 'both', thresholds: [8, 12],
    text: k('{n} or more life will be healed between both Avatars.') },
  emperor: { title: k('The Emperor'), art: `${TAROT}/emperor.webp`, stat: 'uniques', seat: 'both', thresholds: [5, 8],
    text: k('{n} or more Unique cards will be played this match.') },
  hermit: { title: k('The Hermit'), art: `${TAROT}/hermit.webp`, stat: 'sites', seat: 'both', thresholds: [12, 16],
    text: k('{n} or more sites will be played this match.') },
  hanged: { title: k('The Hanged Man'), art: `${TAROT}/hanged-man.webp`, stat: 'manaSpent', seat: 'self', thresholds: [25, 35],
    text: k('You will spend {n} or more mana this match.') },
  devil: { title: k('The Devil'), art: `${TAROT}/devil.webp`, stat: 'damageTaken', seat: 'opponent', thresholds: [12, 16],
    text: k('You will deal {n} or more damage to the enemy Avatar.') },
  insight: { title: k('The Star'), art: `${TAROT}/star.webp`, stat: 'damageTaken', seat: 'self', insight: true,
    text: k('You will win this match without taking any damage.') },
};

// The prediction's two faces, called at the initiative roll.
export const PREDICTIONS = {
  win: { title: k('Victory'), art: `${TAROT}/sun.webp` },
  loss: { title: k('Defeat'), art: `${TAROT}/tower.webp` },
};

const SET_LABEL = { gothic: k('Gothic'), beta: k('Beta'), arthurian: k('Arthurian Legends') };

export function rewardLabel(reward) {
  if (!reward) return '';
  switch (reward.kind) {
    case 'insight': return t('Fully restore your Foresight');
    case 'coins': return t('{n} Gold', { n: reward.amount });
    case 'arcana': return t('{n} Arcana', { n: reward.amount });
    case 'xp': return t('{n} XP', { n: reward.amount });
    case 'cards': return reward.amount === 1 ? t('1 random card') : t('{n} random cards', { n: reward.amount });
    case 'pack': {
      const set = t(SET_LABEL[reward.setKey] || reward.setKey);
      return reward.amount === 1 ? t('1 {set} pack', { set }) : t('{n} {set} packs', { n: reward.amount, set });
    }
    default: return '';
  }
}

export function omenTarget(omen) {
  const def = OMENS[omen?.id];
  if (!def || def.insight) return 0;
  return def.thresholds[(omen.tier || 1) - 1];
}

export function omenText(omen) {
  const def = OMENS[omen?.id];
  return def ? t(def.text, { n: omenTarget(omen) }) : '';
}

const other = (seat) => (seat === 'p1' ? 'p2' : 'p1');

// Live progress of a chosen omen from the match mirror's `stats`.
export function omenProgress(omen, stats, seat) {
  const def = OMENS[omen?.id];
  if (!def) return { value: 0, target: 0, done: false };
  const read = (s) => stats?.[s]?.[def.stat] || 0;
  const value = def.seat === 'both' ? read('p1') + read('p2') : def.seat === 'opponent' ? read(other(seat)) : read(seat);
  if (def.insight) return { value, target: 0, done: value === 0 };
  const target = omenTarget(omen);
  return { value, target, done: value >= target };
}

const has = (state, key) => Boolean(state?.pointsSpent?.includes(key));
export const isOracle = (state) => state?.classKey === 'oracle';
export const canPredict = (state) => isOracle(state) && has(state, 'oracle_peek');
export const canChooseOmen = (state) => isOracle(state) && has(state, 'oracle_branching');
export const canRerollOmens = (state) => canChooseOmen(state) && has(state, 'oracle_better');
export const hasReading = (state) => canPredict(state) || canChooseOmen(state);

export function maxForesight(state) {
  if (!isOracle(state)) return 0;
  if (has(state, 'oracle_akashicality')) return 500;
  let max = BASE_MAX_FORESIGHT;
  for (const key of ['oracle_expanse_mind', 'oracle_expanse_horizon', 'oracle_expanse_centre', 'oracle_expanse_unique']) {
    if (has(state, key)) max += 5;
  }
  if (has(state, 'oracle_expanse_written')) max += 10;
  return max;
}

export function sightCostFor(state, rarity) {
  const node = SIGHT_NODE[rarity];
  if (!node || !isOracle(state) || !has(state, node)) return null;
  return SIGHT_COST[rarity];
}

// Server calls. Each answers { foresight, maxForesight, pending, rerollCost }.
export const fetchReading = (roomCode) => api.post('/profile/me/oracle/omens', { roomCode });
export const rerollReading = (roomCode) => api.post('/profile/me/oracle/reroll', { roomCode });
export const commitReading = (roomCode, omenId, prediction) => api.post('/profile/me/oracle/commit', { roomCode, omenId, prediction });
export const rerollPackCard = (packId, cardIndex) => api.post(`/profile/me/packs/${encodeURIComponent(packId)}/reroll`, { cardIndex });
